import React, { useState, useContext, useEffect, useCallback } from 'react';
import CheckIn from './CheckIn';
import CheckOut from './CheckOut';
import AdultsDropdown from './AdultsDropdown';
import KidsDropdown from './KidsDropdown';
import { RoomContext } from '../context/RoomContext';
import { SpinnerDotted } from 'spinners-react';
import {
  formatLocalDate,
  getDefaultStayDates,
  isValidStayRange,
  normalizeStayPayload,
  parseLocalDate,
  addDays,
} from '../utils/dateUtils';

const BookForm = () => {
  const {
    adults,
    kids,
    handleClick,
    loadAvailableRooms,
  } = useContext(RoomContext);

  const defaults = getDefaultStayDates();
  const [checkIn, setCheckIn] = useState(defaults.checkIn);
  const [checkOut, setCheckOut] = useState(defaults.checkOut);
  const [loading, setLoading] = useState(false);

  const searchRooms = useCallback(
    async (checkInValue, checkOutValue) => {
      const { checkInDate, checkOutDate } = normalizeStayPayload(checkInValue, checkOutValue);
      if (!isValidStayRange(checkInDate, checkOutDate)) return;

      setLoading(true);
      try {
        await loadAvailableRooms({
          checkIn: checkInDate,
          checkOut: checkOutDate,
          guestCount: Number(adults) + Number(kids),
          markSearched: true,
        });
      } finally {
        setLoading(false);
      }
    },
    [adults, kids, loadAvailableRooms]
  );

  // Re-run the search whenever the guest count changes (including on first mount)
  // so results always match the selected adults/kids without requiring an explicit
  // "Check Now" click — otherwise switching to a smaller party size than the initial
  // fetch used can wrongly show "no rooms available" until the button is clicked.
  useEffect(() => {
    searchRooms(checkIn, checkOut);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adults, kids]);

  const handleCheckInChange = (date) => {
    if (!date) return;
    setCheckIn(date);

    const checkInStr = formatLocalDate(date);
    const checkOutStr = formatLocalDate(checkOut);
    if (!isValidStayRange(checkInStr, checkOutStr)) {
      setCheckOut(parseLocalDate(addDays(checkInStr, 1)));
    }
  };

  const handleCheckOutChange = (date) => {
    if (!date) return;
    setCheckOut(date);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { checkInDate, checkOutDate } = normalizeStayPayload(checkIn, checkOut);

    if (!isValidStayRange(checkInDate, checkOutDate)) {
      alert('Check-out date must be later than check-in date.');
      return;
    }

    if (!handleClick(e, checkInDate, checkOutDate)) return;

    await searchRooms(checkIn, checkOut);
  };

  const minCheckOutDate = parseLocalDate(addDays(formatLocalDate(checkIn), 1));

  return (
    <div className="relative w-full p-4 bg-[#FFFFE7] shadow-lg rounded-lg">
      {loading && (
        <div className="absolute inset-0 bg-black/40 flex justify-center items-center z-10 rounded-lg">
          <SpinnerDotted size={50} color="white" />
        </div>
      )}

      <form onSubmit={handleSubmit} className="relative">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:gap-3">
          <div className="grid grid-cols-2 gap-3 lg:contents">
            <div className="flex items-center border border-gray-300 rounded-lg p-3 bg-gradient-to-r from-gray-50 to-gray-100 shadow-sm lg:flex-1">
              <CheckIn selectedDate={checkIn} onChangeDate={handleCheckInChange} />
            </div>

            <div className="flex items-center border border-gray-300 rounded-lg p-3 bg-gradient-to-r from-gray-50 to-gray-100 shadow-sm lg:flex-1">
              <CheckOut
                selectedDate={checkOut}
                onChangeDate={handleCheckOutChange}
                minDate={minCheckOutDate}
              />
            </div>

            <div className="flex items-center border border-gray-300 rounded-lg p-3 bg-gradient-to-r from-gray-50 to-gray-100 shadow-sm lg:flex-1">
              <AdultsDropdown />
            </div>

            <div className="flex items-center border border-gray-300 rounded-lg p-3 bg-gradient-to-r from-gray-50 to-gray-100 shadow-sm lg:flex-1">
              <KidsDropdown />
            </div>
          </div>

          <button
            type="submit"
            className={`w-full lg:w-auto ${
              loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-yellow-500 to-yellow-600'
            } text-white font-bold py-3 px-6 rounded-lg shadow-md hover:shadow-lg transition-transform duration-300 transform hover:scale-105`}
            disabled={loading}
          >
            {loading ? 'Checking...' : 'Check Now'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BookForm;
